import { isDefined } from "@clipboard-health/util-ts";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EventIcon from "@mui/icons-material/Event";
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { isAfter, isBefore, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { forwardRef, useCallback, useState } from "react";

import { type WorkplaceShift } from "../api/useGetWorkplaceShifts";

type ShiftStatus = "Completed" | "In Progress" | "Scheduled";

interface WorkplaceShiftItemProps {
  shift: WorkplaceShift;
  selected: boolean;
  copied?: boolean;
  onClick: () => void;
}

function getShiftStatus(shift: WorkplaceShift): ShiftStatus {
  const now = new Date();

  if (!isDefined(shift.startAt) || !isDefined(shift.endAt)) {
    return "Scheduled";
  }

  const startDate = parseISO(shift.startAt);
  const endDate = parseISO(shift.endAt);

  if (shift.hasClockOut || isAfter(now, endDate)) {
    return "Completed";
  }

  if (shift.hasClockIn || (isAfter(now, startDate) && isBefore(now, endDate))) {
    return "In Progress";
  }

  return "Scheduled";
}

function getStatusColor(status: ShiftStatus): "success" | "warning" | "info" {
  switch (status) {
    case "Completed": {
      return "success";
    }
    case "In Progress": {
      return "warning";
    }
    case "Scheduled": {
      return "info";
    }
    default: {
      return "info";
    }
  }
}

export const WorkplaceShiftItem = forwardRef<HTMLDivElement, WorkplaceShiftItemProps>(
  (props, ref) => {
    const { shift, selected, copied: copiedFromKeyboard = false, onClick } = props;
    const [copiedFromClick, setCopiedFromClick] = useState(false);

    const isCopied = copiedFromKeyboard || copiedFromClick;

    const timezone = shift.workplaceTimezone ?? "America/Los_Angeles";
    const status = getShiftStatus(shift);
    const statusColor = getStatusColor(status);

    const handleCopy = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        void navigator.clipboard.writeText(shift.id);
        setCopiedFromClick(true);
        setTimeout(() => {
          setCopiedFromClick(false);
        }, 2000);
      },
      [shift.id]
    );

    const formatShiftDate = (): string => {
      if (!isDefined(shift.startAt)) {
        return "Unknown date";
      }

      return formatInTimeZone(parseISO(shift.startAt), timezone, "MMM d, yyyy");
    };

    const formatShiftTime = (): string => {
      if (!isDefined(shift.startAt) || !isDefined(shift.endAt)) {
        return "Unknown time";
      }

      const startTime = formatInTimeZone(parseISO(shift.startAt), timezone, "h:mm a");
      const endTime = formatInTimeZone(parseISO(shift.endAt), timezone, "h:mm a");

      return `${startTime} - ${endTime}`;
    };

    const primaryLabel = [shift.qualificationName, formatShiftDate()].filter(isDefined).join(" • ");

    return (
      <ListItemButton
        ref={ref}
        selected={selected}
        sx={{
          py: 0.75,
          pl: 6,
          pr: 2,
          "&.Mui-selected": {
            backgroundColor: "action.selected",
          },
        }}
        onClick={onClick}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <EventIcon color={statusColor} fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Text variant="body2">{primaryLabel}</Text>
              <Text
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette[statusColor].main,
                  fontWeight: 500,
                })}
              >
                {status}
              </Text>
            </Box>
          }
          secondary={
            <Text variant="caption" color="text.secondary">
              {formatShiftTime()}
              {isDefined(shift.workerName) && ` • ${shift.workerName}`}
              {!isDefined(shift.workerName) && " • Unassigned"}
            </Text>
          }
        />
        <Tooltip title={isCopied ? "Copied!" : "Copy Shift ID"}>
          <IconButton size="small" color={isCopied ? "success" : "default"} onClick={handleCopy}>
            {isCopied ? (
              <CheckIcon sx={{ fontSize: 16 }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </ListItemButton>
    );
  }
);

WorkplaceShiftItem.displayName = "WorkplaceShiftItem";
